import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, MapPinIcon, ArrowRightIcon } from 'lucide-react';
import MaintenanceModal from './MaintenanceModal';
import { useGetWorkOrderInteractions } from '../../../custom-hooks/use-maintenance';
import { URLS } from '../../../router-x/routes';
import { formatNumberWithCommasPakistani } from '../../../utils/functions';
import { CATEGORY_META, PRIORITY_META, STATUS_META, getLocationLabel, getInitials, isOverdue } from '../utils';

const RECENT_ACTIVITY_LIMIT = 3;

// A fast, at-a-glance peek from the board — deliberately reduced compared to the
// full detail page (no attachments or full comment thread, no editing) so it stays
// a quick check rather than a second place to do the same work as the full page.
const QuickViewModal = ({ isOpen, onClose, workOrder, canViewCosts }) => {
    const navigate = useNavigate();
    const { data: interactions = [] } = useGetWorkOrderInteractions(isOpen ? workOrder?._id : null);
    const recentActivity = interactions.slice(-RECENT_ACTIVITY_LIMIT).reverse();

    if (!workOrder) return null;

    const overdue = isOverdue(workOrder);

    const goToFullDetails = () => {
        onClose();
        navigate(`${URLS.ADMIN.MAINTENANCE}/${workOrder._id}`);
    };

    return (
        <MaintenanceModal isOpen={isOpen} onClose={onClose} title={workOrder.title} maxWidth="sm">
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-inv-cream-100 px-3 py-1 text-xs font-bold text-inv-ink">{STATUS_META[workOrder.status]?.label}</span>
                    <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${PRIORITY_META[workOrder.priority]?.badgeClass || 'bg-gray-50 text-gray-600'}`}>
                        {PRIORITY_META[workOrder.priority]?.label}
                    </span>
                    <span className="rounded-md bg-gray-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                        {CATEGORY_META[workOrder.category]?.label}
                    </span>
                </div>

                <div className="flex flex-col gap-1.5 text-sm text-[#6B675F]">
                    <div className="flex items-center gap-2">
                        <MapPinIcon size={15} className="shrink-0 text-[#8C8175]" />
                        {getLocationLabel(workOrder)}
                    </div>
                    <div className={`flex items-center gap-2 ${overdue ? 'font-semibold text-red-600' : ''}`}>
                        <CalendarIcon size={15} className="shrink-0 text-[#8C8175]" />
                        Due {new Date(workOrder.dueDate).toLocaleDateString()}{overdue ? ' (Overdue)' : ''}
                    </div>
                </div>

                {workOrder.description && (
                    <p className="whitespace-pre-wrap text-sm text-[#6B675F] line-clamp-3">{workOrder.description}</p>
                )}

                <div className="flex items-center justify-between rounded-xl bg-inv-cream-100/60 px-3 py-2.5">
                    <div className="flex items-center gap-2 text-sm text-inv-ink">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-inv-brand-light text-[11px] font-bold text-inv-brand">
                            {getInitials(workOrder.assignee?.name)}
                        </span>
                        {workOrder.assignee?.name || 'Unassigned'}
                    </div>
                    {canViewCosts && (
                        <span className="text-sm font-bold text-inv-ink">
                            {workOrder.estimate != null
                                ? `${formatNumberWithCommasPakistani(workOrder.estimate)} PKR`
                                : 'No estimate'}
                        </span>
                    )}
                </div>

                {recentActivity.length > 0 && (
                    <div>
                        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#8C8175]">Recent Activity</p>
                        <div className="flex flex-col gap-1.5">
                            {recentActivity.map((entry) => (
                                <p key={entry._id} className="text-xs text-[#6B675F]">
                                    {entry.type === 'comment' ? (
                                        <><span className="font-semibold text-inv-ink">{entry.sender?.name || 'Unknown'}:</span> {entry.message}</>
                                    ) : (
                                        <span className="italic text-[#8C8175]">
                                            <span className="font-semibold not-italic">{entry.sender?.name || 'Someone'}</span> — {entry.message}
                                        </span>
                                    )}
                                </p>
                            ))}
                        </div>
                    </div>
                )}

                <button
                    onClick={goToFullDetails}
                    className="flex items-center justify-center gap-1.5 rounded-xl bg-inv-brand px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-inv-brand-dark"
                >
                    View Full Details <ArrowRightIcon size={15} />
                </button>
            </div>
        </MaintenanceModal>
    );
};

export default QuickViewModal;
